import type {
  BomMergeResponse,
  CircuitOp,
  CommitResponse,
  DrcReport,
  DrcSeverity,
  ImportDiffResponse,
  IrResponse,
  LineageResponse,
  MutateResponse,
  SnapshotResponse,
} from "@models/circuit-ir";
import { toast } from "@stores/toastStore";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { circuitsApi } from "../services/api";
/**
 * React Query hooks over the Circuit IR HTTP surface
 * (`backend/circuits/router.py`).
 *
 * Two access patterns:
 *
 *   * Read-only on a committed `:SchematicVersion` — used by the
 *     project detail pages (graph / BOM / DRC tabs).
 *   * Read + write on a `circuit_ir_snapshot` (working buffer) — used
 *     by the Drafts feature where the agent + UI iterate before commit.
 *
 * Snapshot IDs are globally-unique UUIDs; no org-scope wrapper needed.
 * Server data only: no Zustand duplication.
 */
import { circuitKeys } from "../services/queryKeys";

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

// ── Version-scoped reads ────────────────────────────────────────────────────

/** Hydrate the IR for a committed `:SchematicVersion`. */
export function useVersionIr(versionId: string | null | undefined) {
  return useQuery<IrResponse>({
    queryKey: circuitKeys.versionIr(versionId ?? ""),
    queryFn: () => circuitsApi.getVersionIr(versionId ?? ""),
    enabled: !!versionId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Run structural DRC on a committed version. Cheap (~ms). */
export function useVersionDrc(
  versionId: string | null | undefined,
  severity?: DrcSeverity,
) {
  return useQuery<DrcReport>({
    queryKey: circuitKeys.versionDrc(versionId ?? "", severity),
    queryFn: () => circuitsApi.getVersionDrc(versionId ?? "", severity),
    enabled: !!versionId,
  });
}

/** IR-based diff between two versions of the same project. */
export function useImportDiff(
  fromVersionId: string | null | undefined,
  toVersionId: string | null | undefined,
) {
  return useQuery<ImportDiffResponse>({
    queryKey: circuitKeys.importDiff(fromVersionId ?? "", toVersionId ?? ""),
    queryFn: () =>
      circuitsApi.getImportDiff(fromVersionId ?? "", toVersionId ?? ""),
    enabled: !!fromVersionId && !!toVersionId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Rename-detection diff between an uncommitted snapshot (typically
 *  produced by a plugin push) and the project's currently-committed
 *  version. Different endpoint shape from `useImportDiff` — see the
 *  `getSnapshotImportDiff` API method comment. */
export function useSnapshotImportDiff(
  snapshotId: string | null | undefined,
  projectId: string | null | undefined,
  boardId?: string | null,
  circuitId?: string | null,
) {
  return useQuery<ImportDiffResponse>({
    queryKey: [
      "snapshot-import-diff",
      snapshotId ?? "",
      projectId ?? "",
      boardId ?? "",
      circuitId ?? "",
    ],
    queryFn: () =>
      circuitsApi.getSnapshotImportDiff(
        snapshotId ?? "",
        projectId ?? "",
        boardId ?? null,
        circuitId ?? null,
      ),
    enabled: !!snapshotId && !!projectId,
    staleTime: 60 * 1000,
  });
}

// ── Snapshot lifecycle ──────────────────────────────────────────────────────

/** Read a snapshot's IR by ID. */
export function useSnapshot(snapshotId: string | null | undefined) {
  return useQuery<SnapshotResponse>({
    queryKey: circuitKeys.snapshot(snapshotId ?? ""),
    queryFn: () => circuitsApi.getSnapshot(snapshotId ?? ""),
    enabled: !!snapshotId,
  });
}

/** DRC on a snapshot's IR. The unfiltered finding list is the default —
 *  filtering by severity is a presentation choice. */
export function useSnapshotDrc(
  snapshotId: string | null | undefined,
  severity?: DrcSeverity,
) {
  return useQuery<DrcReport>({
    queryKey: circuitKeys.snapshotDrc(snapshotId ?? "", severity),
    queryFn: () => circuitsApi.getSnapshotDrc(snapshotId ?? "", severity),
    enabled: !!snapshotId,
  });
}

/** Walk the snapshot's parent_id chain backward (newest-first). */
export function useSnapshotLineage(snapshotId: string | null | undefined) {
  return useQuery<LineageResponse>({
    queryKey: circuitKeys.snapshotLineage(snapshotId ?? ""),
    queryFn: () => circuitsApi.getSnapshotLineage(snapshotId ?? ""),
    enabled: !!snapshotId,
  });
}

/** Seed a snapshot from a committed version — the typical opening move
 *  when the agent starts editing a real circuit. */
export function useCreateSnapshotFromVersion() {
  return useMutation({
    mutationFn: ({
      sessionId,
      versionId,
    }: {
      sessionId: string;
      versionId: string;
    }) => circuitsApi.createSnapshotFromVersion(sessionId, versionId),
    onError: (err) => {
      toast.error(errorMessage(err, "Could not seed snapshot"));
    },
  });
}

/** Apply typed CircuitOps to a snapshot. The new snapshot is cached
 *  under its own key; the parent is left untouched. */
export function useMutateSnapshot() {
  const qc = useQueryClient();
  return useMutation<
    MutateResponse,
    Error,
    { snapshotId: string; ops: CircuitOp[]; createdByEventId?: string }
  >({
    mutationFn: ({ snapshotId, ops, createdByEventId }) =>
      circuitsApi.mutateSnapshot(snapshotId, ops, createdByEventId),
    onSuccess: (data) => {
      qc.setQueryData<SnapshotResponse>(
        circuitKeys.snapshot(data.snapshot_id),
        {
          snapshot_id: data.snapshot_id,
          session_id: "",
          parent_id: data.parent_snapshot_id,
          content_hash: "",
          ir: data.ir,
        },
      );
      void qc.invalidateQueries({
        queryKey: circuitKeys.snapshotLineage(data.snapshot_id),
      });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Mutation rejected"));
    },
  });
}

/** Commit a snapshot as a new `:SchematicVersion`. The 422 path carries
 *  the DRC report — let callers inspect via `error.detail.drc_report`. */
export function useCommitSnapshot() {
  const qc = useQueryClient();
  return useMutation<
    CommitResponse,
    Error,
    {
      snapshotId: string;
      project_id: string;
      /** Per-Circuit target (Phase 14). Optional; backend defaults to
       *  the project's main circuit when absent. */
      circuit_id?: string;
      parent_version_id?: string | null;
      description?: string;
      semver_label?: string;
      set_current?: boolean;
      drc_gate?: boolean;
    }
  >({
    mutationFn: ({ snapshotId, ...payload }) =>
      circuitsApi.commitSnapshot(snapshotId, payload),
    onSuccess: (data, vars) => {
      toast.success(
        `Committed ${data.semver_label ?? `v${data.version_number}`}`,
      );
      // Bust every read that depends on the project's / circuit's
      // version chain. Multi-Circuit projects also need the per-Circuit
      // version list invalidated so the timeline picks up the new
      // version without a manual refresh.
      void qc.invalidateQueries({
        queryKey: ["versions", "project", vars.project_id],
      });
      if (vars.circuit_id) {
        void qc.invalidateQueries({
          queryKey: ["circuits", "circuit", vars.circuit_id, "versions"],
        });
      }
      void qc.invalidateQueries({ queryKey: ["projects", "list"] });
      void qc.invalidateQueries({
        queryKey: ["projects", "stats"],
      });
    },
    onError: (err) => {
      toast.error(errorMessage(err, "Commit failed"));
    },
  });
}

/** Merge a BOM CSV into a snapshot. Returns a new snapshot + a report
 *  of matched / sch_only / bom_only / conflicts / duplicates. */
export function useMergeBomIntoSnapshot() {
  return useMutation<
    BomMergeResponse,
    Error,
    { snapshotId: string; csv: File }
  >({
    mutationFn: ({ snapshotId, csv }) =>
      circuitsApi.mergeBomIntoSnapshot(snapshotId, csv),
    onError: (err) => {
      toast.error(errorMessage(err, "BOM merge failed"));
    },
  });
}

// ── Download URL helpers ────────────────────────────────────────────────────

export function exportKicadNetUrl(versionId: string): string {
  return circuitsApi.exportKicadNetUrl(versionId);
}

export function exportSnapshotBomCsvUrl(snapshotId: string): string {
  return circuitsApi.exportSnapshotBomCsvUrl(snapshotId);
}
